import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CommuteRoundedIcon from '@mui/icons-material/CommuteRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import { MarkdownText } from '../common/MarkdownText';
import { emphasizeMarkdownLead } from '../../utils/markdown';

type CityStop = {
  city?: string;
  country?: string;
  city_key?: string;
  day_start?: number;
  day_end?: number;
  reason?: string | null;
  transport_notes?: string | null;
};

interface MultiCityRouteNotesProps {
  cities?: CityStop[];
  isMultiCity?: boolean;
}

const formatDayRange = (start?: number, end?: number): string | null => {
  if (typeof start !== 'number' || typeof end !== 'number') return null;
  if (start === end) return `Day ${start}`;
  return `Days ${start}–${end}`;
};

export const MultiCityRouteNotes: React.FC<MultiCityRouteNotesProps> = ({ cities, isMultiCity = false }) => {
  const theme = useTheme();
  const stops = (cities || []).filter((city) => city?.city);

  if (!isMultiCity || stops.length === 0) {
    return null;
  }

  const sorted = [...stops].sort((a, b) => {
    const startA = typeof a.day_start === 'number' ? a.day_start : Number.MAX_SAFE_INTEGER;
    const startB = typeof b.day_start === 'number' ? b.day_start : Number.MAX_SAFE_INTEGER;
    if (startA !== startB) return startA - startB;
    return (a.city || '').localeCompare(b.city || '');
  });

  const hasDetails = sorted.some((city) => city.transport_notes);
  if (!hasDetails) {
    return null;
  }

  const borderTint = alpha(theme.palette.primary.main, 0.12);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 4,
        border: `1px solid ${borderTint}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.secondary.main, 0.16),
              '& svg': {
                fontSize: 20,
                color: theme.palette.secondary.main,
              },
            }}
          >
            <RouteRoundedIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Getting between cities
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              How you'll move from one stop to the next during your trip.
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={2}>
          {sorted.map((city, index) => {
            const dayLabel = formatDayRange(city.day_start, city.day_end);
            return (
              <Box
                key={city.city_key || `${city.city}-${index}`}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${borderTint}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {city.city}
                    {city.country ? `, ${city.country}` : ''}
                  </Typography>
                  {dayLabel && (
                    <Chip
                      size="small"
                      label={dayLabel}
                      sx={{
                        backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                        color: theme.palette.secondary.dark,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Stack>

                {city.transport_notes && (
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.25 }}>
                    <CommuteRoundedIcon sx={{ fontSize: 18, color: 'primary.main', mt: '2px' }} />
                    <MarkdownText variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      {emphasizeMarkdownLead(city.transport_notes)}
                    </MarkdownText>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};
